import { useRef, useState } from 'react';
import {
  Box,
  Button,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

interface PostAnalysisDataProps {
  onClose: () => void;
  data: string;
}

const CAPTURE_ATTR = 'data-capture-id';
const CAPTURE_ID = 'post-analysis-content';

export const PostAnalysisData = ({ onClose, data }: PostAnalysisDataProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!contentRef.current) return;

    setDownloading(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: contentRef.current.scrollWidth,
        windowHeight: contentRef.current.scrollHeight,
        onclone: (doc) => {
          const cloneEl = doc.querySelector<HTMLElement>(
            `[${CAPTURE_ATTR}="${CAPTURE_ID}"]`
          );
          if (cloneEl) {
            cloneEl.style.maxHeight = 'none';
            cloneEl.style.overflowY = 'visible';
          }
        },
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = -(imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
      pdf.save('analysis.pdf');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box>
      <Box
        ref={contentRef}
        {...{ [CAPTURE_ATTR]: CAPTURE_ID }}
        sx={{
          backgroundColor: '#fff',
          padding: 3,
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {data}
        </ReactMarkdown>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button onClick={handleDownload} variant="outlined" sx={{ mr: 2 }} disabled={downloading}>
          Download
        </Button>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </Box>
    </Box>
  );
};
